import { useMemo } from 'react';

import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import { useWorkloadsOptimizationSummaryQuery } from '@cast/features-cluster-shared';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { toBig } from '@cast/utils';

export type SavingsSummaryData = {
  estimatedMonthlySavings: number | undefined;
  estimatedMonthlySavingsPercentage: number | undefined;
  currentMonthlySavings: number | undefined;
  currentMonthlySavingsPercentage: number | undefined;
};

export const useSavingsSummaryData = () => {
  const { cluster } = useCluster();
  const { data, isLoading, isFetching, error, refetch } =
    useWorkloadsOptimizationSummaryQuery({
      clusterId: cluster.id,
      enabled: !!cluster.id,
    });

  const savingsData = useMemo<SavingsSummaryData | undefined>(() => {
    const costs = data?.costsPerHour;
    if (!costs) return undefined;

    const recommended = costs.recommended ?? 0;
    const requested = costs.requested ?? 0;
    const originalRequested = costs.originalRequested ?? 0;

    const estimatedMonthlySavings = toBig(requested)
      .sub(recommended)
      .mul(AVERAGE_HOURS_IN_MONTH)
      .toNumber();

    const currentMonthlyCost = toBig(requested)
      .mul(AVERAGE_HOURS_IN_MONTH)
      .toNumber();

    const estimatedMonthlySavingsPercentage = currentMonthlyCost
      ? toBig(estimatedMonthlySavings)
          .div(currentMonthlyCost)
          .mul(100)
          .toNumber()
      : 0;

    const currentMonthlySavings = toBig(originalRequested)
      .sub(requested)
      .mul(AVERAGE_HOURS_IN_MONTH)
      .toNumber();

    const originalMonthlyCost = toBig(originalRequested)
      .mul(AVERAGE_HOURS_IN_MONTH)
      .toNumber();

    const currentMonthlySavingsPercentage = originalMonthlyCost
      ? toBig(currentMonthlySavings)
          .div(originalMonthlyCost)
          .mul(100)
          .toNumber()
      : 0;

    return {
      estimatedMonthlySavings,
      estimatedMonthlySavingsPercentage,
      currentMonthlySavings,
      currentMonthlySavingsPercentage,
    };
  }, [data]);

  return { data: savingsData, isLoading, isFetching, error, refetch };
};
